import {
  IconAB2,
  IconAdjustmentsHorizontal,
  IconCar,
  IconChartDots,
  IconChartGridDots,
  IconClock24,
  IconEyeBolt,
  IconEyePause,
  IconEyeSearch,
  IconFocusAuto,
  IconInfoCircle,
  IconPhotoSensor2,
  IconRobot,
  IconRobotOff,
  IconVolume2,
  IconVolumeOff,
  IconWorldWww,
} from "@tabler/icons-react";
import { TextOrientation, VFABehaviour } from "types/tests";
import {
  AllConfigurationKeys,
  FDTBehaviourType,
  CPTConfigMode,
  EyeType,
  GoldmannSize,
  TestLanguage,
  TestType,
  TestCategory,
  OptionalVFATests,
  WaitDuration,
  ThresholdLevel,
  KineticPerimetryPattern,
  DyscalculiaConfigMode,
} from "types/testTypes";
import { ConfigurationMetadata } from "types/uiTypes";

type EnumKeys<T> = T extends Record<infer K, any> ? K : never;

type PrefixedEnumKeys<T, Prefix extends string> =
  T extends Record<infer K, any> ? `${Prefix}.${K & string}` : never;

type ConfigurationEnumKeys =
  | EnumKeys<typeof TestType>
  | EnumKeys<typeof EyeType>
  | EnumKeys<typeof TestLanguage>
  | EnumKeys<typeof FDTBehaviourType>
  | EnumKeys<typeof GoldmannSize>
  | EnumKeys<typeof CPTConfigMode>
  | EnumKeys<typeof DyscalculiaConfigMode>
  | PrefixedEnumKeys<typeof TestCategory, "TestCategory">
  | EnumKeys<typeof VFABehaviour>
  | PrefixedEnumKeys<typeof OptionalVFATests, "OptionalVFATests">
  | EnumKeys<typeof WaitDuration>
  | EnumKeys<typeof ThresholdLevel>
  | EnumKeys<typeof KineticPerimetryPattern>
  | EnumKeys<typeof TextOrientation>;

export const COLOR_VISION_LEGAND = {
  nothing: "N",
  "2_lines": "2L",
  no_lines: "NL",
  "1_line": "1L",
  trace_line: "TL",
};

export const CONFIGURATION_VALUE_LABEL_KEY_MAP: Record<
  ConfigurationEnumKeys,
  {
    labelKey: string;
    groupKey?:
      | "TestGroup.standard"
      | "TestGroup.screening"
      | "TestGroup.ptosis"
      | "TestGroup.binocular"
      | "TestGroup.threshold"
      | "TestGroup.blueOnYellow"
      | "TestGroup.kinetic"
      | "TestGroup.suprathreshold";
  }
> = {
  Threshold: {
    labelKey: "TestType.threshold",
  },
  Ascending: {
    labelKey: "TestType.ascending",
  },
  PelliRobson: {
    labelKey: "TestType.pelliRobson",
  },
  TumblingE: {
    labelKey: "TestType.tumblingE",
  },
  TumblingEThreshold: {
    labelKey: "TestType.tumblingEThreshold",
  },
  ReadingAssessment: {
    labelKey: "TestType.RA",
  },
  ContinuousPerformance: {
    labelKey: "TestType.continuousPerformance",
  },
  GoNoGo: {
    labelKey: "TestType.goNoGo",
  },
  IdenticalPairs: {
    labelKey: "TestType.identicalPairs",
  },
  Logmar: {
    labelKey: "TestType.logmar",
  },
  Chart: {
    labelKey: "TestType.chart",
  },
  Pupillometry: {
    labelKey: "TestType.pupillometry",
  },
  RAPD: {
    labelKey: "TestType.RAPD",
  },
  CircularPursuit: {
    labelKey: "TestType.circularPursuit",
  },
  XIH: {
    labelKey: "TestType.xih",
  },
  SmoothPursuit: {
    labelKey: "TestType.smoothPursuit",
  },
  C_20_1: {
    labelKey: "TestType.C-20-1",
  },
  C_20_5: {
    labelKey: "TestType.C-20-5",
  },
  N_30_1: {
    labelKey: "TestType.N-30-1",
  },
  N_30_5: {
    labelKey: "TestType.N-30-5",
  },
  CoverUncover: {
    labelKey: "TestType.coverUncover",
  },
  ObjectivePhoria: {
    labelKey: "TestType.objectivePhoria",
  },
  Staircase: {
    labelKey: "TestType.staircase",
  },
  Videonystagmography: {
    labelKey: "TestType.videonystagmography",
  },
  OptokineticNystagmus: {
    labelKey: "TestType.optokineticNystagmus",
  },
  PER_24_2: {
    labelKey: "TestType.24-2",
    groupKey: "TestGroup.standard",
  },
  PER_24_2C: {
    labelKey: "TestType.24-2C",
    groupKey: "TestGroup.standard",
  },
  PER_30_2: {
    labelKey: "TestType.30-2",
    groupKey: "TestGroup.standard",
  },
  PER_10_2: {
    labelKey: "TestType.10-2",
    groupKey: "TestGroup.standard",
  },
  PER_BINOCULAR: {
    labelKey: "TestType.BINOCULAR",
    groupKey: "TestGroup.binocular",
  },
  PER_BINOCULAR2: {
    labelKey: "TestType.BINOCULAR2",
    groupKey: "TestGroup.binocular",
  },
  PER_BINOCULAR3: {
    labelKey: "TestType.BINOCULAR3",
    groupKey: "TestGroup.binocular",
  },
  PER_C_40: {
    labelKey: "TestType.C-40",
    groupKey: "TestGroup.screening",
  },
  PER_C_76: {
    labelKey: "TestType.C-76",
    groupKey: "TestGroup.screening",
  },
  PER_SUPERIOR36: {
    labelKey: "TestType.SUPERIOR36",
    groupKey: "TestGroup.ptosis",
  },
  PER_SUPERIOR64: {
    labelKey: "TestType.SUPERIOR64",
    groupKey: "TestGroup.ptosis",
  },
  PER_FF_120: {
    labelKey: "TestType.FF_120",
    groupKey: "TestGroup.screening",
  },
  VisualField: {
    labelKey: "TestType.visualField",
  },
  FDT: {
    labelKey: "TestType.FDT",
  },
  ContrastSensitivity: {
    labelKey: "TestType.CS",
  },
  ColorVision: {
    labelKey: "TestType.Ishihara",
  },
  ColorVisionAutomatic: {
    labelKey: "colorVisionAutomatic",
  },
  VisualAcuity: {
    labelKey: "TestType.visualAcuity",
  },
  BothEye: {
    labelKey: "bothEye",
  },
  RightEye: {
    labelKey: "rightEye",
  },
  LeftEye: {
    labelKey: "leftEye",
  },
  RightThenLeft: {
    labelKey: "rightThenLeft",
  },
  NOT_SPECIFIED: {
    labelKey: "notSpecified",
  },
  English: {
    labelKey: "english",
  },
  Turkish: {
    labelKey: "turkish",
  },
  Arabic: {
    labelKey: "arabic",
  },
  French: {
    labelKey: "french",
  },
  Czech: {
    labelKey: "czech",
  },
  German: {
    labelKey: "german",
  },
  Greek: {
    labelKey: "greek",
  },
  Spanish: {
    labelKey: "spanish",
  },
  Persian: {
    labelKey: "persian",
  },
  Italian: {
    labelKey: "italian",
  },
  Japanese: {
    labelKey: "japanese",
  },
  Korean: {
    labelKey: "korean",
  },
  Malay: {
    labelKey: "malay",
  },
  Romanian: {
    labelKey: "romanian",
  },
  Swedish: {
    labelKey: "swedish",
  },
  Urdu: {
    labelKey: "urdu",
  },
  Vietnamese: {
    labelKey: "vietnamese",
  },
  Chinese: {
    labelKey: "chinese",
  },
  Bulgarian: {
    labelKey: "bulgarian",
  },
  Danish: {
    labelKey: "danish",
  },
  Hungarian: {
    labelKey: "hungarian",
  },
  NorwegianBokmal: {
    labelKey: "norwegianBokmal",
  },
  Dutch: {
    labelKey: "dutch",
  },
  Polish: {
    labelKey: "polish",
  },
  Portuguese: {
    labelKey: "portuguese",
  },
  Russian: {
    labelKey: "russian",
  },
  Thai: {
    labelKey: "thai",
  },
  Latvian: {
    labelKey: "latvian",
  },
  Finnish: {
    labelKey: "finnish",
  },
  Indonesian: {
    labelKey: "indonesian",
  },
  SupraThreshold: {
    labelKey: "TestController.testBehaviour_4",
  },
  GOLDMANN_III: {
    labelKey: "TestController.fixationSize_3",
  },
  GOLDMANN_IV: {
    labelKey: "TestController.fixationSize_4",
  },
  GOLDMANN_V: {
    labelKey: "TestController.fixationSize_5",
  },
  Default: {
    labelKey: "TestController.cptConfigModeDefault",
  },
  Demo: {
    labelKey: "TestController.cptConfigModeDemo",
  },
  Adult: {
    labelKey: "adult",
  },
  AdultAdaptive: {
    labelKey: "adultAdaptive",
  },
  AdultAdaptiveFast: {
    labelKey: "adultAdaptiveFast",
  },
  AdultDemo: {
    labelKey: "TestController.cptConfigModeAdultDemo",
  },
  thresholdLevel5: {
    labelKey: "5 dB",
  },
  thresholdLevel10: {
    labelKey: "10 dB",
  },
  thresholdLevel15: {
    labelKey: "15 dB",
  },
  thresholdLevel20: {
    labelKey: "20 dB",
  },
  "TestCategory.VisualField": {
    labelKey: "TestType.VFA",
  },
  "TestCategory.FDT": {
    labelKey: "TestType.FDT",
  },
  "TestCategory.ContrastSensitivity": {
    labelKey: "TestType.CS",
  },
  "TestCategory.VisualAcuity": {
    labelKey: "TestType.VA",
  },
  "TestCategory.ColorVision": {
    labelKey: "TestType.CV",
  },
  "TestCategory.AmslerGrid": {
    labelKey: "TestType.AG",
  },
  "TestCategory.Pupillometry": {
    labelKey: "TestType.PL",
  },
  "TestCategory.Dyslexia": {
    labelKey: "TestType.DY",
  },
  "TestCategory.ReadingAssessment": {
    labelKey: "TestType.RA",
  },
  "TestCategory.ContinuousPerformance": {
    labelKey: "TestType.CPT",
  },
  "TestCategory.OcularMotility": {
    labelKey: "TestType.OM",
  },
  "TestCategory.Cover": {
    labelKey: "TestType.COV",
  },
  "TestCategory.StereoAcuity": {
    labelKey: "TestType.SA",
  },
  "TestCategory.Videonystagmography": {
    labelKey: "TestType.VSG",
  },
  "TestCategory.Kinetic": {
    labelKey: "TestType.KIN",
  },
  TOP: {
    labelKey: "TestController.testBehaviour_5",
    groupKey: "TestGroup.threshold",
  },
  ADAPTIVE_TRACKING: {
    labelKey: "TestController.testBehaviour_9",
    groupKey: "TestGroup.threshold",
  },
  FAST_4_2: {
    labelKey: "",
  },
  FAST_4: {
    labelKey: "",
  },
  FULL_THRESH_4: {
    labelKey: "TestController.testBehaviour_2",
    groupKey: "TestGroup.threshold",
  },
  SWAP_TOP: {
    labelKey: "TestType.SWAP",
    groupKey: "TestGroup.blueOnYellow",
  },
  SITA_LIKE: {
    labelKey: "TestController.testBehaviour_3",
    groupKey: "TestGroup.threshold",
  },
  SIMPLE_TEST: {
    labelKey: "TestController.testBehaviour_4",
    groupKey: "TestGroup.suprathreshold",
  },
  TWO_ZONE: {
    labelKey: "TestController.testBehaviour_7",
    groupKey: "TestGroup.suprathreshold",
  },
  THREE_ZONE: {
    labelKey: "TestController.testBehaviour_8",
    groupKey: "TestGroup.suprathreshold",
  },
  "OptionalVFATests.SWAP": {
    labelKey: "",
  },
  "OptionalVFATests.FDT": {
    labelKey: "",
  },
  "OptionalVFATests.KINETIC": {
    labelKey: "",
  },
  "OptionalVFATests.KIN": {
    labelKey: "",
  },
  Standard: {
    labelKey: "TestController.standardSpeed",
  },
  Slow: {
    labelKey: "TestController.slowSpeed",
  },
  Slower: {
    labelKey: "TestController.slowerSpeed",
  },
  AutoKinetic: {
    labelKey: "TestType.autoKinetic",
  },
  FF_CUSTOM: {
    labelKey: "TestController.FF_CUSTOM",
  },
  FF_60_12: {
    labelKey: "TestController.FF_60_12",
  },
  FF_50_12: {
    labelKey: "TestController.FF_50_12",
  },
  FF_30_8: {
    labelKey: "TestController.FF_30_8",
  },
  NumericalCognition: {
    labelKey: "TestType.numericalCognition",
  },
  "TestCategory.Dyscalculia": {
    labelKey: "TestType.DC",
  },
  WCST: {
    labelKey: "TestType.WCST",
  },
  "TestCategory.WisconsinCardSorting": {
    labelKey: "TestType.WCST",
  },
  NearPointConvergence: {
    labelKey: "TestType.nearPointConvergence",
  },
  AntiSaccades: {
    labelKey: "TestType.antiSaccade",
  },
  "TestCategory.BinocularVision": {
    labelKey: "TestType.BVT",
  },
  "TestCategory.OptokineticNystagmus": {
    labelKey: "TestType.OKN",
  },
  Child: {
    labelKey: "child",
  },
  R2L: {
    labelKey: "rightToLeft",
  },
  L2R: {
    labelKey: "leftToRight",
  },
};

/**
 * Mapping of configuration keys to their UI data
 * such as class names, label keys, icons, and confirmation dialog settings.
 */
export const CONFIGURATION_SELECTOR_UI_DATA_MAP: Record<
  AllConfigurationKeys,
  ConfigurationMetadata
> = {
  patternSize: {
    className: "config-selector-pattern-size",
    labelKey: "",
  },
  testSpeed: {
    className: "config-selector-speed",
    labelKey: "TestController.testSpeed",
    representativeIcon: IconClock24,
    confirmationDialog: {
      titleDescriptionKeys: {
        [WaitDuration.Standard]: {
          titleKey: "TestController.defaultMustWait-.DialogTitle.400",
          descriptionKey: "TestController.defaultMustWait-.DialogDesc.400",
        },
        [WaitDuration.Slow]: {
          titleKey: "TestController.defaultMustWait-.DialogTitle.800",
          descriptionKey: "TestController.defaultMustWait-.DialogDesc.800",
        },
        [WaitDuration.Slower]: {
          titleKey: "TestController.defaultMustWait-.DialogTitle.1000",
          descriptionKey: "TestController.defaultMustWait-.DialogDesc.1000",
        },
      },
    },
  },
  loopCount: {
    className: "config-selector-loop-count",
    labelKey: "",
  },
  pauseDuration: {
    className: "config-selector-pause-duration",
    labelKey: "",
  },
  depth: {
    className: "config-selector-depth",
    labelKey: "",
  },
  radiusDegrees: {
    className: "config-selector-radius-degrees",
    labelKey: "",
  },
  frequencyHz: {
    className: "config-selector-frequency-hz",
    labelKey: "",
  },
  clockwiseDuration: {
    className: "config-selector-clockwise-duration",
    labelKey: "",
  },
  counterClockwiseDuration: {
    className: "config-selector-counter-clockwise-duration",
    labelKey: "",
  },
  stages: {
    className: "config-selector-stages",
    labelKey: "",
  },
  isForAdult: {
    className: "config-selector-is-for-adult",
    labelKey: "",
  },
  isDemoMode: {
    className: "config-selector-is-demo-mode",
    labelKey: "",
  },
  isTriggerClickEnabled: {
    className: "config-selector-is-trigger-click-enabled",
    labelKey: "",
  },
  cptConfigMode: {
    className: "config-selector-cpt-config-mode",
    labelKey: "TestController.cptConfigMode",
    representativeIcon: IconAdjustmentsHorizontal,
  },
  dyscalculiaConfigMode: {
    className: "config-selector-dyscalculia-config-mode",
    labelKey: "dyscalculiaConfigMode",
    representativeIcon: IconAdjustmentsHorizontal,
  },
  textOrientation: {
    className: "config-selector-text-orientation",
    labelKey: "textOrientation",
  },
  lapCount: {
    className: "config-selector-lap-count",
    labelKey: "",
  },
  initialWaitDuration: {
    className: "config-selector-initial-wait-duration",
    labelKey: "",
  },
  stimulusShowDuration: {
    className: "config-selector-stimulus-show-duration",
    labelKey: "",
  },
  recoveryDuration: {
    className: "config-selector-recovery-duration",
    labelKey: "",
  },
  blindSpotTesting: {
    className: "config-selector-blind-spot-testing",
    labelKey: "TestController.enableBsTest",
    descriptionKey: "TestController.bsTestText",
    toggleIcons: { on: IconAB2, off: null },
    confirmationDialog: {
      titleDescriptionKeys: {
        positive: {
          titleKey: "TestController.blindSpotTesting-.DialogTitle.true",
          descriptionKey: "TestController.blindSpotTesting-.DialogDesc.true",
        },
        negative: {
          titleKey: "TestController.blindSpotTesting-.DialogTitle.false",
          descriptionKey: "TestController.blindSpotTesting-.DialogDesc.false",
        },
      },
    },
  },
  testBehaviour: {
    className: "config-selector-test-behaviour",
    labelKey: "TestController.testBehaviour",
    representativeIcon: IconChartDots,
  },
  blindSpotMonitoringEnabled: {
    className: "config-selector-blind-spot-monitoring-enabled",
    labelKey: "TestController.enableBsMonitoring",
    descriptionKey: "TestController.bsMonitoringText",
    toggleIcons: { on: IconEyeSearch, off: null },
    confirmationDialog: {
      titleDescriptionKeys: {
        positive: {
          titleKey: "TestController.blindSpotMonitoringEnabled-.DialogTitle.true",
          descriptionKey: "TestController.blindSpotMonitoringEnabled-.DialogDesc.true",
        },
        negative: {
          titleKey: "TestController.blindSpotMonitoringEnabled-.DialogTitle.false",
          descriptionKey: "TestController.blindSpotMonitoringEnabled-.DialogDesc.false",
        },
      },
    },
  },
  fovealThresholdTestingEnabled: {
    className: "config-selector-foveal-threshold-testing-enabled",
    labelKey: "TestController.fovealThresholdTesting",
    descriptionKey: "TestController.fovealThresholdTestingText",
    toggleIcons: { on: IconFocusAuto, off: null },
    confirmationDialog: {
      titleDescriptionKeys: {
        positive: {
          titleKey: "TestController.fovealThresholdTestingEnabled-.DialogTitle.true",
          descriptionKey: "TestController.fovealThresholdTestingEnabled-.DialogDesc.true",
        },
        negative: {
          titleKey: "TestController.fovealThresholdTestingEnabled-.DialogTitle.false",
          descriptionKey: "TestController.fovealThresholdTestingEnabled-.DialogDesc.false",
        },
      },
    },
  },
  enablePausingTheTestWithButton: {
    className: "config-selector-enable-pausing-the-test-with-button",
    labelKey: "TestController.enablePausingTheTestWithButton",
  },
  showTutorial: {
    className: "config-selector-show-tutorial",
    labelKey: "TestController.tutorialCheck",
  },
  fixationSize: {
    className: "config-selector-fixation-size",
    labelKey: "TestController.fixationSize",
    confirmationDialog: {
      titleDescriptionKeys: {
        [GoldmannSize.GOLDMANN_III]: {
          titleKey: "TestController.fixationSize-.DialogTitle.3",
          descriptionKey: "TestController.fixationSize-.DialogDesc.3",
        },
        [GoldmannSize.GOLDMANN_IV]: {
          titleKey: "TestController.fixationSize-.DialogTitle.4",
          descriptionKey: "TestController.fixationSize-.DialogDesc.4",
        },
        [GoldmannSize.GOLDMANN_V]: {
          titleKey: "TestController.fixationSize-.DialogTitle.5",
          descriptionKey: "TestController.fixationSize-.DialogDesc.5",
        },
      },
    },
  },
  stimuliSize: {
    className: "config-selector-stimuli-size",
    labelKey: "TestController.stimuliSize",
    confirmationDialog: {
      titleDescriptionKeys: {
        [GoldmannSize.GOLDMANN_III]: {
          titleKey: "TestController.stimuliSize-.DialogTitle.3",
          descriptionKey: "TestController.stimuliSize-.DialogDesc.3",
        },
        [GoldmannSize.GOLDMANN_IV]: {
          titleKey: "TestController.stimuliSize-.DialogTitle.4",
          descriptionKey: "TestController.stimuliSize-.DialogDesc.4",
        },
        [GoldmannSize.GOLDMANN_V]: {
          titleKey: "TestController.stimuliSize-.DialogTitle.5",
          descriptionKey: "TestController.stimuliSize-.DialogDesc.5",
        },
      },
    },
  },
  dbColor: {
    className: "config-selector-db-color",
    labelKey: "TestController.thresholdLevel",
    representativeIcon: IconAdjustmentsHorizontal,
  },
  constants: {
    className: "config-selector-constants",
    labelKey: "",
  },
  behaviourType: {
    className: "config-selector-behaviour-type",
    labelKey: "TestController.testBehaviour",
  },
  eyeType: {
    className: "config-selector-eye-type",
    labelKey: "TestController.eye",
    representativeIcon: IconEyeBolt,
  },
  isTwoScreenFixationEnabled: {
    className: "config-selector-two-screen-fixation",
    labelKey: "TestController.enableTwoScreenFixation",
    descriptionKey: "TestController.enableTwoScreenFixationText",
    toggleIcons: { on: IconPhotoSensor2, off: null },
    confirmationDialog: {
      titleDescriptionKeys: {
        positive: {
          titleKey: "TestController.twoScreenFixation-.DialogTitle.true",
          descriptionKey: "TestController.twoScreenFixation-.DialogDesc.true",
        },
        negative: {
          titleKey: "TestController.twoScreenFixation-.DialogTitle.false",
          descriptionKey: "TestController.twoScreenFixation-.DialogDesc.false",
        },
      },
    },
  },
  isCalibrationNeeded: {
    className: "config-selector-is-calibration-needed",
    labelKey: "TestController.calibrationNeeded",
    descriptionKey: "TestController.calibrationNeededText",
    toggleIcons: { on: IconFocusAuto, off: null },
  },
  startWithCalibrationScene: {
    className: "config-selector-start-with-calibration-scene",
    labelKey: "TestController.startWithCalibrationScene",
    toggleIcons: { on: IconFocusAuto, off: null },
  },
  autoPauseForGaze: {
    className: "config-selector-auto-pause-for-gaze",
    labelKey: "TestController.EnableAutoPause",
    descriptionKey: "TestController.enableAutoPauseText",
    toggleIcons: { on: IconEyePause, off: null },
    confirmationDialog: {
      titleDescriptionKeys: {
        positive: {
          titleKey: "TestController.autoPauseForGaze-.DialogTitle.true",
          descriptionKey: "TestController.autoPauseForGaze-.DialogDesc.true",
        },
        negative: {
          titleKey: "TestController.autoPauseForGaze-.DialogTitle.false",
          descriptionKey: "TestController.autoPauseForGaze-.DialogDesc.false",
        },
      },
    },
  },
  quickAssistant: {
    className: "config-selector-quick-assistant",
    labelKey: "",
  },
  isVoiceAssistantEnabled: {
    className: "config-selector-is-voice-assistant-enabled",
    labelKey: "",
  },
  voiceAssistantConfig: {
    className: "config-selector-voice-assistant-config",
    labelKey: "TestController.voiceAssistant",
    toggleIcons: { on: IconVolume2, off: IconVolumeOff },
  },
  showAssistant: {
    className: "config-selector-show-assistant",
    labelKey: "",
  },
  assistantLanguage: {
    className: "config-selector-assistant-language",
    labelKey: "",
  },
  testName: {
    className: "config-selector-test-name",
    labelKey: "TestController.testType",
  },
  videoInfos: {
    className: "config-selector-video-infos",
    labelKey: "",
  },
  shouldPlayButtonClickSound: {
    className: "config-selector-should-play-button-click-sound",
    labelKey: "",
  },
  name: {
    className: "config-selector-name",
    labelKey: "testCategory",
  },
  testType: {
    className: "config-selector-test-type",
    labelKey: "TestController.testType",
    representativeIcon: IconChartGridDots,
  },
  isPressToStartEnabled: {
    className: "config-selector-press-to-start",
    labelKey: "",
  },
  eyeConfiguration: {
    className: "config-selector-eye-configuration",
    labelKey: "",
  },
  testLanguage: {
    className: "config-selector-test-language",
    labelKey: "TestController.language",
    representativeIcon: IconWorldWww,
  },
  virtualAssistanceConfig: {
    className: "config-selector-virtual-assistance-config",
    labelKey: "TestController.virtualAssistant",
    toggleIcons: { on: IconRobot, off: IconRobotOff },
  },
  extraData: {
    className: "config-selector-extra-data",
    labelKey: "",
  },
  speed: {
    className: "config-selector-test-specific-speed",
    labelKey: "",
  },
  testPattern: {
    className: "config-selector-test-specific-pattern",
    labelKey: "TestController.testPatterns",
    representativeIcon: IconChartGridDots,
  },
  testTypeCircleAngle: {
    className: "config-selector-test-type-circle-angle",
    labelKey: "startAngle",
    numberInputMinimum: 10,
    numberInputMaximum: 70,
    numberInputStep: 10,
  },
  numberOfStimuliStarts: {
    className: "config-selector-number-of-stimuli-starts",
    labelKey: "stimuliPointCounts",
    numberInputMinimum: 4,
    numberInputMaximum: 360,
    numberInputStep: 1,
  },
  fixationShiftAngle: {
    className: "config-selector-fixation-shift-angle",
    labelKey: "",
  },
  trialCount: {
    className: "config-selector-trial-count",
    labelKey: "trialCount",
  },
  ruleChangeWindow: {
    className: "config-selector-rule-change-window",
    labelKey: "",
  },
  testIds: {
    className: "config-selector-reading-assessment-test-ids",
    labelKey: "readingAssessmentTestIds",
  },
  textListType: {
    className: "config-selector-reading-assessment-text-list-type",
    labelKey: "Text",
  },
  showComprehensionQuestions: {
    className: "config-selector-show-comprehension-questions",
    labelKey: "TestController.showComprehensionQuestions",
    toggleIcons: { on: IconInfoCircle, off: null },
  },
  childMode: {
    className: "config-selector-child-mode",
    labelKey: "TestController.childMode",
    descriptionKey: "TestController.childModeText",
    toggleIcons: { on: IconCar, off: null },
  },
  plateCount: {
    className: "config-selector-color-vision-plate-count",
    labelKey: "plateCount",
  },
  questionsJson: {
    className: "config-selector-color-vision-questions-json",
    labelKey: "questionsJson",
  },
  isBackToBackEyeTest: {
    className: "config-selector-back-to-back-eye-test",
    labelKey: "backToBackEyeTest",
  },
  fixationDurationMinSeconds: {
    className: "config-selector-fixation-duration-min-seconds",
    labelKey: "",
  },
  fixationDurationMaxSeconds: {
    className: "config-selector-fixation-duration-max-seconds",
    labelKey: "",
  },
  interTrialMinSeconds: {
    className: "config-selector-inter-trial-min-seconds",
    labelKey: "",
  },
  interTrialMaxSeconds: {
    className: "config-selector-inter-trial-max-seconds",
    labelKey: "",
  },
  peripheralVisibleBeforeGuideSeconds: {
    className: "config-selector-peripheral-visible-before-guide-seconds",
    labelKey: "",
  },
  guidingMoveToCenterDurationSeconds: {
    className: "config-selector-guiding-move-to-center-duration-seconds",
    labelKey: "",
  },
  minHorizontalDegrees: {
    className: "config-selector-min-horizontal-degrees",
    labelKey: "",
  },
  maxHorizontalDegrees: {
    className: "config-selector-max-horizontal-degrees",
    labelKey: "",
  },
  screenDistanceMeters: {
    className: "config-selector-screen-distance-meters",
    labelKey: "",
  },
  pauseTest: {
    className: "config-selector-pause-test",
    labelKey: "",
  },
};
